import React, { useCallback, useEffect, useRef, useState } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';

/** Survives toolbar remounts (e.g. DataGrid slots/key churn) so scroll position is kept. */
const scrollLeftByYearsKey = new Map();

/**
 * Reusable Year Tabs Selector Component
 * Provides a tabular year selection interface similar to ResultAnalysis
 *
 * @param {string} activeYear - Currently selected year (as string)
 * @param {Array<string>} availableYears - Array of available years (as strings)
 * @param {Function} onChange - Callback when year changes: (event, newYear) => void
 * @param {Object} sx - Additional sx props for styling
 */
const YearTabsSelector = ({
  activeYear,
  availableYears = [],
  onChange,
  sx = {},
}) => {
  const rootRef = useRef(null);
  const allowMuiScrollRef = useRef(false);
  const yearsKeyRef = useRef('');
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [selectedOffStart, setSelectedOffStart] = useState(false);
  const [selectedOffEnd, setSelectedOffEnd] = useState(false);

  const safeYears = Array.isArray(availableYears)
    ? availableYears.filter(year => year != null && year !== '')
    : [];
  const yearsKey = safeYears.join('|');
  yearsKeyRef.current = yearsKey;

  const safeActiveYear = safeYears.includes(activeYear)
    ? activeYear
    : safeYears[safeYears.length - 1];

  const handleChange = (event, newYear) => {
    if (newYear && onChange) {
      allowMuiScrollRef.current = true;
      onChange(event, newYear);
    }
  };

  const showScroll = safeYears.length > 3;

  const syncScrollState = useCallback(() => {
    const scroller = rootRef.current?.querySelector('.MuiTabs-scroller');
    if (!scroller) return;

    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const scrollLeft = scroller.scrollLeft;
    setAtStart(scrollLeft <= 1);
    setAtEnd(maxScroll <= 1 || scrollLeft >= maxScroll - 1);
    scrollLeftByYearsKey.set(yearsKeyRef.current, scrollLeft);

    const selected = scroller.querySelector('.MuiTab-root.Mui-selected');
    if (!selected || maxScroll <= 1) {
      setSelectedOffStart(false);
      setSelectedOffEnd(false);
      return;
    }
    const sRect = scroller.getBoundingClientRect();
    const tRect = selected.getBoundingClientRect();
    setSelectedOffStart(tRect.left < sRect.left - 1);
    setSelectedOffEnd(tRect.right > sRect.right + 1);
  }, []);

  const restoreScroll = useCallback(() => {
    const scroller = rootRef.current?.querySelector('.MuiTabs-scroller');
    if (!scroller) return;
    const saved = scrollLeftByYearsKey.get(yearsKeyRef.current);
    if (saved != null && scroller.scrollLeft !== saved) {
      scroller.scrollLeft = saved;
    }
    syncScrollState();
  }, [syncScrollState]);

  useEffect(() => {
    if (!showScroll) {
      setAtStart(true);
      setAtEnd(true);
      setSelectedOffStart(false);
      setSelectedOffEnd(false);
      return undefined;
    }

    const root = rootRef.current;
    const scroller = root?.querySelector('.MuiTabs-scroller');
    if (!scroller) return undefined;

    const afterMuiScroll = (fn) => {
      requestAnimationFrame(fn);
      setTimeout(fn, 0);
      setTimeout(fn, 50);
      setTimeout(fn, 200);
      setTimeout(fn, 400);
    };

    if (allowMuiScrollRef.current) {
      allowMuiScrollRef.current = false;
      afterMuiScroll(syncScrollState);
    } else if (scrollLeftByYearsKey.has(yearsKeyRef.current)) {
      // Remount / grid re-render: keep the user's tab scroll, don't jump to selected.
      restoreScroll();
      afterMuiScroll(restoreScroll);
    } else {
      // First visit for this year set — allow MUI to bring selected into view, then persist.
      afterMuiScroll(syncScrollState);
    }

    const onScroll = () => syncScrollState();
    scroller.addEventListener('scroll', onScroll, { passive: true });

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncScrollState) : null;
    ro?.observe(scroller);

    const buttons = Array.from(root.querySelectorAll('.MuiTabs-scrollButtons'));
    const onArrowClick = () => afterMuiScroll(syncScrollState);
    buttons.forEach((btn) => btn.addEventListener('click', onArrowClick));

    return () => {
      scroller.removeEventListener('scroll', onScroll);
      buttons.forEach((btn) => btn.removeEventListener('click', onArrowClick));
      ro?.disconnect();
    };
  }, [showScroll, yearsKey, safeActiveYear, syncScrollState, restoreScroll]);

  if (safeYears.length === 0) {
    return null;
  }

  return (
    <Box
      ref={rootRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        maxWidth: showScroll ? 260 : undefined,
        minWidth: 0,
        ...sx,
        // Drive disabled look from scroll position — MUI's IntersectionObserver (0.99
        // threshold) misses end-of-scroll with subpixel tab widths.
        '& .MuiTabs-scrollButtons': {
          opacity: 1,
        },
        '& .MuiTabs-scrollButtons:first-of-type': {
          opacity: atStart ? 0.3 : 1,
          pointerEvents: atStart ? 'none' : 'auto',
          color: selectedOffStart ? 'primary.main' : 'action.active',
        },
        '& .MuiTabs-scrollButtons:last-of-type': {
          opacity: atEnd ? 0.3 : 1,
          pointerEvents: atEnd ? 'none' : 'auto',
          color: selectedOffEnd ? 'primary.main' : 'action.active',
        },
        '& .MuiTabs-scrollButtons.Mui-disabled, & .MuiTabScrollButton-disabled': {
          opacity: 0.3,
        },
      }}
    >
      <Tabs
        value={safeActiveYear}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons={showScroll}
        allowScrollButtonsMobile
        sx={{
          minHeight: 40,
          height: 40,
          width: '100%',
          minWidth: 0,
          alignSelf: 'flex-end',
          borderBottom: 'none',
          '& .MuiTabs-indicator': {
            bottom: 0,
            height: 2
          },
          '& .MuiTab-root': {
            minHeight: 40,
            height: 40,
            padding: '8px 12px',
            fontSize: '0.875rem',
            lineHeight: 1.5,
            textTransform: 'none',
            fontWeight: 400,
            margin: 0,
            textAlign: 'center',
            justifyContent: 'center'
          }
        }}
      >
        {safeYears.map((year, index) => {
          const safeKey = year != null ? String(year) : `year-${index}`;
          return (
            <Tab
              key={safeKey}
              label={year}
              value={year}
              sx={{
                minWidth: 60,
                '&.Mui-selected': {
                  fontWeight: 500
                }
              }}
            />
          );
        })}
      </Tabs>
    </Box>
  );
};

export default YearTabsSelector;
